import React, { useCallback, useEffect, useMemo, useRef } from "react";
import CircularProgress from "@mui/material/CircularProgress";

const FONT = "'DM Sans', 'Segoe UI', sans-serif";
const TEXT_PRIMARY = "#111827";
const TEXT_SECONDARY = "#6b7280";
const TEXT_TERTIARY = "#9ca3af";
const BORDER = "0.5px solid #e5e7eb";
const INPUT_BORDER = "0.5px solid #d1d5db";

const CHART_CDN = "https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.js";
const DM_SANS_ID = "vp-points-dashboard-dm-sans";

function loadScriptOnce(src, id) {
  return new Promise((resolve, reject) => {
    if (typeof window.Chart !== "undefined") {
      resolve();
      return;
    }
    const existing = document.getElementById(id);
    if (existing) {
      if (typeof window.Chart !== "undefined") resolve();
      else existing.addEventListener("load", () => resolve(), { once: true });
      return;
    }
    const s = document.createElement("script");
    s.id = id;
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });
}

function ensureDmSans() {
  if (document.getElementById(DM_SANS_ID)) return;
  const link = document.createElement("link");
  link.id = DM_SANS_ID;
  link.rel = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&display=swap";
  document.head.appendChild(link);
}

function formatRowDate(row) {
  const raw = row.occurredUtc || row.submittedUtc || row.createdUtc;
  if (!raw) return "—";
  try {
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
  } catch {
    return "—";
  }
}

function formatUtcCell(raw) {
  if (!raw) return "—";
  try {
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
  } catch {
    return "—";
  }
}

function formatUpdatedBy(row) {
  const name = row.reviewedByDisplayName || row.ReviewedByDisplayName;
  if (name && String(name).trim()) return String(name).trim();
  const id = row.reviewedByAzureId || row.ReviewedByAzureId;
  if (id && String(id).trim()) return String(id).trim();
  return "—";
}

function formatPointsCell(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function employeeInitials(name) {
  const s = (name || "").trim();
  if (!s) return "?";
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
  }
  return s.slice(0, 2).toUpperCase();
}

const AVATAR_BG_PALETTE = [
  "#185FA5",
  "#378ADD",
  "#534AB7",
  "#639922",
  "#1D9E75",
  "#BA7517",
  "#D4537E",
  "#3C3489",
];

function avatarBgForKey(key) {
  const s = String(key || "");
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_BG_PALETTE[h % AVATAR_BG_PALETTE.length];
}

function normalizeStatus(raw) {
  const s = (raw || "").trim();
  if (!s) return "";
  if (s.toLowerCase() === "inreview") return "InReview";
  return s;
}

function statusPillStyle(status) {
  const n = normalizeStatus(status);
  switch (n) {
    case "Submitted":
      return { background: "#E6F1FB", color: "#185FA5" };
    case "InReview":
      return { background: "#FAEEDA", color: "#854F0B" };
    case "Approved":
      return { background: "#EAF3DE", color: "#3B6D11" };
    case "Rejected":
      return { background: "#FCEBEB", color: "#A32D2D" };
    default:
      return { background: "#f3f4f6", color: TEXT_SECONDARY };
  }
}

function statusLabel(status) {
  const n = normalizeStatus(status);
  if (n === "InReview") return "In review";
  return n || "—";
}

/** Max data rows before the grid shows a vertical scrollbar (header stays sticky). */
const GRID_SCROLL_AFTER_ROWS = 12;
const GRID_ROW_EST_PX = 47;
const GRID_HEAD_EST_PX = 44;

function submissionOccurredMs(row) {
  const raw = row.occurredUtc || row.OccurredUtc || row.submittedUtc || row.SubmittedUtc || row.createdUtc || row.CreatedUtc;
  if (!raw) return 0;
  const t = new Date(raw).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function submissionApprovedPoints(row) {
  const status = normalizeStatus(row.status || row.Status);
  if (status !== "Approved") return 0;
  const n = Number(row.approvedPoints ?? row.ApprovedPoints);
  return Number.isNaN(n) ? 0 : n;
}

function submissionEmployeeKey(row) {
  return String(row.employeeAzureId || row.EmployeeAzureId || row.employeeId || row.EmployeeId || "").trim().toLowerCase();
}

function csvEscape(value) {
  const s = value == null ? "" : String(value);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function triggerCsvDownload(csvText, filename) {
  const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Admin Points dashboard shell — KPIs, charts, filters slot, submissions table.
 * Chart.js is loaded from CDN on mount when the donut is rendered.
 */
export default function PointsDashboard({
  totalSubmissions = 0,
  totalSubmissionsHint = "",
  pendingReview = 0,
  approved = 0,
  activeEmployees = 0,
  totalEmployees = 0,
  activityChartRows = [],
  statusDonutSegments = [],
  rows = [],
  onUpdateStatus,
  onAddActivity,
  onRefresh,
  onRowClick,
  loading = false,
  calendarYear,
  yearOptions = [],
  onYearChange,
  calendarMonth = "",
  monthOptions = [],
  onMonthChange,
  statusFilter = "",
  statusFilterOptions = [],
  onStatusFilterChange,
  activityTypeFilter = "",
  activityTypeFilterOptions = [{ value: "", label: "All activity types" }],
  onActivityTypeFilterChange,
  employeeFilterSlot = null,
}) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    ensureDmSans();
  }, []);

  const tableScrollMaxHeight =
    rows.length > GRID_SCROLL_AFTER_ROWS ? GRID_HEAD_EST_PX + GRID_SCROLL_AFTER_ROWS * GRID_ROW_EST_PX : undefined;

  const handleDownloadReport = useCallback(() => {
    if (!rows.length) return;

    // Chronological per employee so Total points can show progressive accumulation.
    const sorted = [...rows].sort((a, b) => {
      const keyCmp = submissionEmployeeKey(a).localeCompare(submissionEmployeeKey(b));
      if (keyCmp !== 0) return keyCmp;
      const timeCmp = submissionOccurredMs(a) - submissionOccurredMs(b);
      if (timeCmp !== 0) return timeCmp;
      const idA = Number(a.submissionId ?? a.SubmissionId) || 0;
      const idB = Number(b.submissionId ?? b.SubmissionId) || 0;
      return idA - idB;
    });

    const runningTotalByEmployee = Object.create(null);
    const headers = [
      "Employee",
      "Employee ID",
      "Points year",
      "Activity type",
      "Title",
      "Description",
      "Approved points",
      "Total points",
      "Occurred date",
      "Status",
      "Modified (UTC)",
      "Updated by",
      "Submission ID",
    ];
    const lines = [headers.map(csvEscape).join(",")];
    sorted.forEach((row) => {
      const empKey = submissionEmployeeKey(row) || "__unknown__";
      const approvedPts = submissionApprovedPoints(row);
      const prev = Number(runningTotalByEmployee[empKey]) || 0;
      const cumulative = prev + approvedPts;
      runningTotalByEmployee[empKey] = cumulative;

      const displayName = (row.employeeDisplayName || row.EmployeeDisplayName || "").trim();
      const approvedRaw = row.approvedPoints ?? row.ApprovedPoints;
      const line = [
        displayName || row.employeeAzureId || row.EmployeeAzureId || "",
        row.employeeId ?? row.EmployeeId ?? "",
        row.pointsYear ?? row.PointsYear ?? calendarYear ?? "",
        row.displayName || row.DisplayName || "",
        row.title ?? row.Title ?? "",
        row.description ?? row.Description ?? "",
        approvedRaw == null || approvedRaw === "" ? "" : approvedRaw,
        cumulative,
        formatRowDate(row),
        statusLabel(row.status),
        formatUtcCell(row.modifiedUtc || row.ModifiedUtc),
        formatUpdatedBy(row),
        row.submissionId ?? row.SubmissionId ?? "",
      ].map(csvEscape);
      lines.push(line.join(","));
    });
    const stamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
    const monthPart = calendarMonth === "" || calendarMonth == null ? "all" : String(calendarMonth);
    const filename = `points-dashboard-${calendarYear ?? "year"}-${monthPart}-${stamp}.csv`;
    triggerCsvDownload(`\uFEFF${lines.join("\r\n")}`, filename);
  }, [rows, calendarYear, calendarMonth]);

  const donutTotal = useMemo(
    () => statusDonutSegments.reduce((acc, s) => acc + (Number(s.count) || 0), 0),
    [statusDonutSegments]
  );

  const maxActivity = useMemo(
    () => Math.max(1, ...activityChartRows.map((r) => Number(r.count) || 0)),
    [activityChartRows]
  );

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!canvasRef.current || !statusDonutSegments.length) return;
      try {
        await loadScriptOnce(CHART_CDN, "chartjs-441-umd");
      } catch {
        return;
      }
      if (cancelled || typeof window.Chart === "undefined") return;

      const Chart = window.Chart;
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }

      const data = statusDonutSegments.map((s) => Number(s.count) || 0);
      const backgroundColor = statusDonutSegments.map((s) => s.color || "#9ca3af");

      chartRef.current = new Chart(canvasRef.current, {
        type: "doughnut",
        data: {
          labels: statusDonutSegments.map((s) => s.label),
          datasets: [
            {
              data,
              backgroundColor,
              borderWidth: 0,
              hoverOffset: 0,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: true,
          cutout: "72%",
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label(ctx) {
                  const v = ctx.raw ?? 0;
                  const pct = donutTotal ? Math.round((v / donutTotal) * 100) : 0;
                  return `${ctx.label}: ${v} (${pct}%)`;
                },
              },
            },
          },
        },
      });
    };

    run();
    return () => {
      cancelled = true;
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [statusDonutSegments, donutTotal]);

  const pendingPct = totalSubmissions ? Math.round((pendingReview / totalSubmissions) * 100) : 0;
  const approvalRatePct = totalSubmissions ? Math.round((approved / totalSubmissions) * 100) : 0;

  const selectBase = {
    width: "100%",
    boxSizing: "border-box",
    fontFamily: FONT,
    fontSize: 12,
    border: INPUT_BORDER,
    borderRadius: 6,
    padding: "8px 10px",
    color: TEXT_PRIMARY,
    background: "#fff",
    outline: "none",
  };

  const kpiCard = (accent, children) => (
    <div
      style={{
        background: "#f9fafb",
        borderRadius: 8,
        border: "none",
        padding: "1rem 1.25rem",
        borderLeft: `4px solid ${accent}`,
        boxSizing: "border-box",
        minWidth: 0,
      }}
    >
      {children}
    </div>
  );

  return (
    <div
      style={{
        fontFamily: FONT,
        background: "#fff",
        border: BORDER,
        borderRadius: 16,
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        overflow: "hidden",
        color: TEXT_PRIMARY,
        position: "relative",
        minHeight: loading ? 420 : undefined,
      }}
    >
      {loading ? (
        <div
          role="status"
          aria-live="polite"
          aria-busy="true"
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 20,
            background: "rgba(255, 255, 255, 0.78)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            backdropFilter: "blur(1px)",
          }}
        >
          <CircularProgress size={40} thickness={4} sx={{ color: "#3C3489" }} />
          <span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 500, color: TEXT_SECONDARY }}>
            Loading dashboard…
          </span>
        </div>
      ) : null}

      {/* Page header */}
      <div style={{ padding: "20px 20px 16px" }}>
        <h1
          style={{
            margin: 0,
            fontSize: 22,
            fontWeight: 600,
            color: TEXT_SECONDARY,
            letterSpacing: "-0.02em",
          }}
        >
          Points dashboard
        </h1>
        <p style={{ margin: "8px 0 0", fontSize: 13, color: TEXT_SECONDARY, lineHeight: 1.5, maxWidth: 720 }}>
          Review employee Veersa Points submissions. Status updates are limited to InReview, Approved, and Rejected.
        </p>
      </div>

      {/* KPIs */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
          gap: 12,
          padding: "0 20px 16px",
          boxSizing: "border-box",
        }}
        className="vp-points-kpi-grid"
      >
        {kpiCard(
          "#378ADD",
          <>
            <div style={{ fontSize: 11, fontWeight: 500, color: TEXT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Total submissions
            </div>
            <div style={{ fontSize: 28, fontWeight: 600, color: TEXT_PRIMARY, marginTop: 6, lineHeight: 1 }}>{totalSubmissions}</div>
            {totalSubmissionsHint ? (
              <div style={{ fontSize: 12, color: TEXT_TERTIARY, marginTop: 6 }}>{totalSubmissionsHint}</div>
            ) : null}
          </>
        )}
        {kpiCard(
          "#EF9F27",
          <>
            <div style={{ fontSize: 11, fontWeight: 500, color: TEXT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Pending review
            </div>
            <div style={{ fontSize: 28, fontWeight: 600, color: TEXT_PRIMARY, marginTop: 6, lineHeight: 1 }}>{pendingReview}</div>
            <div style={{ fontSize: 12, color: TEXT_TERTIARY, marginTop: 6 }}>{pendingPct}% of total</div>
          </>
        )}
        {kpiCard(
          "#639922",
          <>
            <div style={{ fontSize: 11, fontWeight: 500, color: TEXT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Approved
            </div>
            <div style={{ fontSize: 28, fontWeight: 600, color: TEXT_PRIMARY, marginTop: 6, lineHeight: 1 }}>{approved}</div>
            <div style={{ fontSize: 12, color: TEXT_TERTIARY, marginTop: 6 }}>{approvalRatePct}% approval rate</div>
          </>
        )}
        {kpiCard(
          "#534AB7",
          <>
            <div style={{ fontSize: 11, fontWeight: 500, color: TEXT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Active employees
            </div>
            <div style={{ fontSize: 28, fontWeight: 600, color: TEXT_PRIMARY, marginTop: 6, lineHeight: 1 }}>{activeEmployees}</div>
            <div style={{ fontSize: 12, color: TEXT_TERTIARY, marginTop: 6 }}>
              of {totalEmployees} total
            </div>
          </>
        )}
      </div>

      <style>{`
        @media (max-width: 900px) {
          .vp-points-kpi-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
        }
        @media (max-width: 480px) {
          .vp-points-kpi-grid { grid-template-columns: 1fr !important; }
        }
        .vp-points-filter-wrap .MuiOutlinedInput-root {
          border-radius: 6px;
          font-size: 12px;
          font-family: ${FONT};
        }
        .vp-points-filter-wrap .MuiOutlinedInput-notchedOutline {
          border: 0.5px solid #d1d5db !important;
        }
        .vp-points-filter-wrap .MuiInputBase-root {
          min-height: 36px;
        }
        .vp-points-chart-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; padding: 0 20px 16px; box-sizing: border-box; }
        @media (max-width: 900px) {
          .vp-points-chart-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* Charts */}
      <div className="vp-points-chart-grid">
        <div
          style={{
            border: BORDER,
            borderRadius: 12,
            padding: "16px 18px",
            background: "#fff",
            boxSizing: "border-box",
            minWidth: 0,
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 600, color: TEXT_PRIMARY, marginBottom: 14 }}>Submissions by activity type</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {activityChartRows.map((row) => {
              const c = Number(row.count) || 0;
              const w = Math.round((c / maxActivity) * 100);
              return (
                <div key={row.label} style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                  <div
                    style={{
                      width: 150,
                      flexShrink: 0,
                      fontSize: 12,
                      color: TEXT_PRIMARY,
                      lineHeight: 1.35,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={row.label}
                  >
                    {row.label}
                  </div>
                  <div style={{ flex: 1, minWidth: 0, height: 10, background: "#f3f4f6", borderRadius: 4, overflow: "hidden" }}>
                    <div style={{ width: `${w}%`, height: "100%", background: row.color || "#378ADD", borderRadius: 4 }} />
                  </div>
                  <div style={{ width: 36, textAlign: "right", fontSize: 12, fontWeight: 600, color: TEXT_PRIMARY, flexShrink: 0 }}>
                    {c}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div
          style={{
            border: BORDER,
            borderRadius: 12,
            padding: "16px 18px",
            background: "#fff",
            boxSizing: "border-box",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 600, color: TEXT_PRIMARY, marginBottom: 8 }}>Status breakdown</div>
          <div style={{ position: "relative", height: 200, maxWidth: 280, margin: "0 auto", width: "100%" }}>
            <canvas ref={canvasRef} aria-label="Status breakdown chart" />
          </div>
          <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
            {statusDonutSegments.map((s) => {
              const c = Number(s.count) || 0;
              const pct = donutTotal ? Math.round((c / donutTotal) * 100) : 0;
              return (
                <div
                  key={s.key || s.label}
                  style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, color: TEXT_PRIMARY }}
                >
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: s.color, flexShrink: 0 }} />
                  <span style={{ flex: 1, minWidth: 0 }}>{s.label}</span>
                  <span style={{ fontWeight: 600, flexShrink: 0 }}>{c}</span>
                  <span style={{ color: TEXT_TERTIARY, width: 40, textAlign: "right", flexShrink: 0 }}>{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ padding: "0 20px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "stretch" }}>
          <div style={{ flex: "1 1 140px", minWidth: 0 }}>
            <select
              style={{ ...selectBase, opacity: loading ? 0.65 : 1, cursor: loading ? "wait" : "pointer" }}
              value={calendarYear}
              disabled={loading}
              onChange={(e) => onYearChange && onYearChange(Number(e.target.value))}
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: "1 1 140px", minWidth: 0 }}>
            <select
              style={{ ...selectBase, opacity: loading ? 0.65 : 1, cursor: loading ? "wait" : "pointer" }}
              value={calendarMonth}
              disabled={loading}
              onChange={(e) => onMonthChange && onMonthChange(e.target.value)}
            >
              {monthOptions.map((m) => (
                <option key={m.value === "" ? "all" : m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "stretch" }}>
          <div style={{ flex: "1 1 140px", minWidth: 0 }}>
            <select
              style={{ ...selectBase, opacity: loading ? 0.65 : 1, cursor: loading ? "wait" : "pointer" }}
              value={statusFilter}
              disabled={loading}
              onChange={(e) => onStatusFilterChange && onStatusFilterChange(e.target.value)}
            >
              {statusFilterOptions.map((s) => (
                <option key={s || "all"} value={s}>
                  {s === "" ? "All statuses" : s}
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: "1 1 140px", minWidth: 0 }}>
            <select
              style={{ ...selectBase, opacity: loading ? 0.65 : 1, cursor: loading ? "wait" : "pointer" }}
              value={activityTypeFilter}
              disabled={loading}
              onChange={(e) => onActivityTypeFilterChange && onActivityTypeFilterChange(e.target.value)}
            >
              {(activityTypeFilterOptions || []).map((opt) => (
                <option key={opt.value === "" ? "all-activities" : opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        {employeeFilterSlot ? (
          <div
            className="vp-points-filter-wrap"
            style={{ width: "100%", pointerEvents: loading ? "none" : "auto", opacity: loading ? 0.65 : 1 }}
          >
            {employeeFilterSlot}
          </div>
        ) : null}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => onRefresh && onRefresh()}
            disabled={loading}
            style={{
              fontFamily: FONT,
              fontSize: 12,
              fontWeight: 500,
              padding: "8px 14px",
              borderRadius: 8,
              border: INPUT_BORDER,
              background: "#fff",
              color: TEXT_PRIMARY,
              cursor: loading ? "wait" : "pointer",
            }}
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
          <button
            type="button"
            onClick={() => onAddActivity && onAddActivity()}
            disabled={loading}
            style={{
              fontFamily: FONT,
              fontSize: 12,
              fontWeight: 500,
              marginLeft: "auto",
              padding: "8px 14px",
              borderRadius: 8,
              border: "none",
              background: "#3C3489",
              color: "#fff",
              cursor: loading ? "wait" : "pointer",
              opacity: loading ? 0.65 : 1,
            }}
          >
            + Add activity
          </button>
        </div>
      </div>

      {/* Table */}
      <div style={{ padding: "0 20px 12px" }}>
        <div
          style={{
            overflowX: "auto",
            overflowY: tableScrollMaxHeight != null ? "auto" : "visible",
            maxHeight: tableScrollMaxHeight != null ? tableScrollMaxHeight : undefined,
            border: BORDER,
            borderRadius: 8,
            boxSizing: "border-box",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              tableLayout: "fixed",
              fontSize: 13,
            }}
          >
            <thead>
              <tr style={{ background: "#fff" }}>
                {[
                  { label: "Employee", w: "15%" },
                  { label: "Total points", w: "9%" },
                  { label: "Activity type", w: "14%" },
                  { label: "Date", w: "12%" },
                  { label: "Status", w: "9%" },
                  { label: "Update DT", w: "13%" },
                  { label: "Updated By", w: "13%" },
                  { label: "Actions", w: "15%" },
                ].map((col) => (
                  <th
                    key={col.label}
                    style={{
                      width: col.w,
                      textAlign: col.label === "Actions" || col.label === "Total points" ? "right" : "left",
                      padding: "10px 12px",
                      fontSize: 11,
                      fontWeight: 600,
                      color: TEXT_TERTIARY,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      borderBottom: BORDER,
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      background: "#fff",
                      boxShadow: tableScrollMaxHeight != null ? "0 1px 0 rgba(0,0,0,0.06)" : undefined,
                    }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={8} style={{ padding: "16px 12px", color: TEXT_SECONDARY, borderBottom: BORDER }}>
                  No rows for the selected filters.
                </td>
              </tr>
            )}
            {rows.map((row, index) => {
              const name =
                (row.employeeDisplayName || row.EmployeeDisplayName || "").trim() ||
                row.employeeAzureId ||
                row.EmployeeAzureId ||
                "—";
              const employeeId = (row.employeeId || row.EmployeeId || "").trim();
              const key = row.employeeAzureId || row.EmployeeAzureId || name;
              const bg = avatarBgForKey(key);
              const ini = employeeInitials(name);
              const st = row.status || "";
              const pill = statusPillStyle(st);
              return (
                <tr
                  key={row.submissionId ?? index}
                  onClick={() => onRowClick && onRowClick(row, index)}
                  style={{
                    cursor: onRowClick ? "pointer" : "default",
                    borderBottom: BORDER,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#f9fafb";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "#fff";
                  }}
                >
                  <td style={{ padding: "10px 12px", verticalAlign: "middle" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                      <div
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          background: bg,
                          color: "#fff",
                          fontSize: 10,
                          fontWeight: 600,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                        aria-hidden
                      >
                        {ini}
                      </div>
                      <div style={{ minWidth: 0, overflow: "hidden" }}>
                        <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: TEXT_PRIMARY }}>
                          {name}
                        </div>
                        {employeeId ? (
                          <div style={{ fontSize: 11, color: TEXT_TERTIARY, overflow: "hidden", textOverflow: "ellipsis" }}>
                            ID: {employeeId}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </td>
                  <td
                    style={{
                      padding: "10px 12px",
                      verticalAlign: "middle",
                      fontSize: 13,
                      fontWeight: 600,
                      color: TEXT_PRIMARY,
                      textAlign: "right",
                    }}
                    title="Sum of approved Veersa Points for this employee in the selected year/month (all activities)"
                  >
                    {formatPointsCell(row.employeeTotalPoints ?? row.EmployeeTotalPoints)}
                  </td>
                  <td
                    style={{
                      padding: "10px 12px",
                      verticalAlign: "middle",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: TEXT_PRIMARY,
                    }}
                    title={row.displayName || ""}
                  >
                    {row.displayName || "—"}
                  </td>
                  <td style={{ padding: "10px 12px", verticalAlign: "middle", color: TEXT_SECONDARY, fontSize: 12 }}>
                    {formatRowDate(row)}
                  </td>
                  <td style={{ padding: "10px 12px", verticalAlign: "middle" }}>
                    <span
                      style={{
                        display: "inline-block",
                        fontSize: 10,
                        fontWeight: 600,
                        padding: "4px 10px",
                        borderRadius: 999,
                        ...pill,
                      }}
                    >
                      {statusLabel(st)}
                    </span>
                  </td>
                  <td
                    style={{
                      padding: "10px 12px",
                      verticalAlign: "middle",
                      color: TEXT_SECONDARY,
                      fontSize: 12,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={formatUtcCell(row.modifiedUtc || row.ModifiedUtc)}
                  >
                    {formatUtcCell(row.modifiedUtc || row.ModifiedUtc)}
                  </td>
                  <td
                    style={{
                      padding: "10px 12px",
                      verticalAlign: "middle",
                      color: TEXT_PRIMARY,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={formatUpdatedBy(row)}
                  >
                    {formatUpdatedBy(row)}
                  </td>
                  <td style={{ padding: "10px 12px", verticalAlign: "middle", textAlign: "right" }}>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onUpdateStatus) onUpdateStatus(row, index);
                      }}
                      style={{
                        fontFamily: FONT,
                        fontSize: 11,
                        fontWeight: 500,
                        padding: "6px 10px",
                        borderRadius: 6,
                        border: "none",
                        background: "#3C3489",
                        color: "#fff",
                        cursor: "pointer",
                      }}
                    >
                      Update status
                    </button>
                  </td>
                </tr>
              );
            })}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10, paddingBottom: 8 }}>
          <button
            type="button"
            onClick={handleDownloadReport}
            disabled={loading || !rows.length}
            title={rows.length ? "Download current grid as CSV" : "No rows to export"}
            style={{
              fontFamily: FONT,
              fontSize: 12,
              fontWeight: 600,
              padding: "8px 16px",
              borderRadius: 8,
              border: "1px solid #3C3489",
              background: "#fff",
              color: "#3C3489",
              cursor: loading || !rows.length ? "not-allowed" : "pointer",
              opacity: loading || !rows.length ? 0.55 : 1,
            }}
          >
            Download report
          </button>
        </div>
      </div>
    </div>
  );
}
